/**
 * Alpine.js component for the property review insights list.
 * Provides client-side filtering, sorting, and infinite scroll.
 */
export function reviewList() {
  return {
    allReviews: [] as any[],
    themeLabels: {} as Record<string, string>,
    guestTypeLabels: {} as Record<string, string>,
    search: "",
    platform: "all",
    sentiment: "all",
    theme: "all",
    source: "all",
    guestType: "",
    sortBy: "date_desc",
    visibleCount: 20,

    init() {
      const el = document.getElementById("review-data");
      if (el?.dataset.reviews) {
        try {
          this.allReviews = JSON.parse(el.dataset.reviews);
        } catch (e) {
          console.error("Failed to parse review data:", e);
          this.allReviews = [];
        }
      }
      if (el?.dataset.themeLabels) {
        try {
          this.themeLabels = JSON.parse(el.dataset.themeLabels);
        } catch (e) {
          this.themeLabels = {};
        }
      }
      if (el?.dataset.guestTypeLabels) {
        try {
          this.guestTypeLabels = JSON.parse(el.dataset.guestTypeLabels);
        } catch (e) {
          this.guestTypeLabels = {};
        }
      }
    },

    /** Translate a theme key to its localized label. */
    themeLabel(key: string): string {
      return this.themeLabels[key] || key;
    },

    /** Translate a guest type key to its localized label. */
    guestTypeLabel(key: string): string {
      return this.guestTypeLabels[key] || key;
    },

    get filteredReviews() {
      let result = this.allReviews;
      if (this.search) {
        const q = this.search.toLowerCase();
        result = result.filter(
          (r: any) =>
            (r.text || "").toLowerCase().includes(q) || (r.author || "").toLowerCase().includes(q),
        );
      }
      if (this.platform !== "all") {
        result = result.filter((r: any) => r.platform === this.platform);
      }
      if (this.sentiment !== "all") {
        result = result.filter((r: any) => r.sentiment === this.sentiment);
      }
      if (this.theme !== "all") {
        result = result.filter((r: any) => r.themes && r.themes.includes(this.theme));
      }
      if (this.source !== "all") {
        result = result.filter((r: any) =>
          this.source === "own" ? !r.isCompetitor : r.isCompetitor,
        );
      }
      if (this.guestType) {
        result = result.filter((r: any) => r.guest_type === this.guestType);
      }
      result = [...result].sort((a: any, b: any) => {
        switch (this.sortBy) {
          case "date_asc":
            return (a.date_sort || "").localeCompare(b.date_sort || "");
          case "rating_high":
            return (b.rating || 0) - (a.rating || 0);
          case "rating_low":
            return (a.rating || 0) - (b.rating || 0);
          default:
            return (b.date_sort || "").localeCompare(a.date_sort || "");
        }
      });
      return result;
    },

    get visibleReviews() {
      return this.filteredReviews.slice(0, this.visibleCount);
    },

    get hasMore() {
      return this.visibleCount < this.filteredReviews.length;
    },

    loadMore() {
      this.visibleCount += 20;
    },

    clearFilters() {
      this.search = "";
      this.platform = "all";
      this.sentiment = "all";
      this.theme = "all";
      this.source = "all";
      this.guestType = "";
    },

    get hasActiveFilters() {
      return (
        this.search ||
        this.platform !== "all" ||
        this.sentiment !== "all" ||
        this.theme !== "all" ||
        this.source !== "all" ||
        this.guestType !== ""
      );
    },
  };
}
